import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { ListBox, Typography } from '@heroui/react';
import { categoryIndentPx, type CategoryTickState } from './categoryPickerMeta';

type CategoryPickerOptionProps = {
    id: string;
    /** What the option reads: its accessible name and its typeahead text. */
    label: string;
    tick: CategoryTickState;
    /** Levels below its section's own row; 0 at the edge. */
    indent?: number;
    /** The part of `label` the search matched, set in bold. */
    match?: { start: number; end: number } | null;
};

/**
 * One category in the category picker (BOS-202): ticked, unticked, or partly
 * ticked with "2 of 19" beside it (the bottom-level categories picked under
 * it). The count is the option's description, so a screen reader hears it
 * after the name; the option itself is unselected until all of it is picked.
 */
export const CategoryPickerOption: React.FC<CategoryPickerOptionProps> = ({
    id,
    label,
    tick,
    indent = 0,
    match = null,
}) => {
    const { t } = useTranslation('pages');
    const isPartial = tick.state === 'partial';

    return (
        <ListBox.Item id={id} textValue={label}>
            <Typography
                className="min-w-0 flex-1"
                slot="label"
                style={{ paddingInlineStart: categoryIndentPx(indent) }}
                truncate
                type="body-sm"
            >
                {match ? (
                    <>
                        {label.slice(0, match.start)}
                        <strong>{label.slice(match.start, match.end)}</strong>
                        {label.slice(match.end)}
                    </>
                ) : (
                    label
                )}
            </Typography>
            {isPartial ? (
                <Typography
                    className="shrink-0"
                    color="muted"
                    slot="description"
                    type="body-xs"
                >
                    {t(
                        'products.components.productTable.filters.categoryPartCount',
                        { picked: tick.picked, total: tick.total },
                    )}
                </Typography>
            ) : null}
            <ListBox.ItemIndicator>
                {isPartial ? (
                    <Icon aria-hidden icon="gravity-ui:minus" />
                ) : undefined}
            </ListBox.ItemIndicator>
        </ListBox.Item>
    );
};
